import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEventStepHistory, type EventStep } from './eventStepHistory';
import { useQuery } from '@tanstack/react-query';
import { styled } from 'styletron-react';
import { ArrowLeft, Phone as PhoneIcon, Minus, Plus, CheckCircle, ScanLine } from 'lucide-react';


import { getPublicEvent, checkEventCode, isEventCodeRejected, eventCodeDenial, sendSmsCode, verifySmsCode, createEventAccess, analyzeDocument, generateWalletPass, clearSmsToken, type CompanionTemplate, type Country, type CodeDeliveryMethod, type PublicEvent } from '../../api/publicApi';
import { TurnstileWidget } from '../../components/TurnstileWidget';
import { useTenantBranding } from '../../hooks/useTenantBranding';
import { toNationalityIsoCode } from '../../hooks/useNationalities';
import { useLang, pickLocalized } from '../../i18n/publicEvents';
import { LanguageToggle } from '../../components/LanguageToggle';
import { slugify } from '../../utils/slug';
import { useS3Image } from '../../hooks/useS3Image';
import { DocumentScanner } from '../../components/DocumentScanner';
import { UploadArea } from '../../components/UploadArea';
import { WalletPassLoader } from '../../components/WalletPassLoader';
import { GuestScanForm, type GuestInfo } from '../../components/GuestScanForm';
import { usePublicPageScroll } from '../../hooks/usePublicPageScroll';
import { usePhoneCountry, sanitizeNationalNumber, CountryCodeButton, CountryPickerModal } from './PhoneCountryPicker';
import { renderMarkdownLite } from '../../utils/markdownLite';
import { effectiveCodeChannel, isWhatsappUnavailable, whatsappCodeEnabled } from '../../utils/codeChannel';
import { isPrivateEvent } from '../../utils/privateEvent';
import { pickEventHero } from '../../utils/eventHero';
import { PrivateLockIcon } from '../../components/PrivateLockIcon';

// --- Styled ---

const Page = styled('div', {
  minHeight: '100vh',
  backgroundColor: '#fff',
  fontFamily: "'Red Hat Text', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  display: 'flex',
  flexDirection: 'column',
});

const TopBar = styled('div', {
  display: 'flex',
  alignItems: 'center',
  padding: '12px 16px',
  gap: '12px',
});

const BackBtn = styled('button', {
  border: 'none', background: 'none', cursor: 'pointer', padding: '4px',
  display: 'flex', alignItems: 'center',
});

const TopBarTitle = styled('span', {
  fontSize: '17px', fontWeight: 500, color: '#000',
});

const HeroImage = styled('div', {
  width: '100%', height: '250px', backgroundColor: '#E5E7EB', overflow: 'hidden',
});

/** Phone-width viewport — where the public page shows the 4:5 mobile banner uncropped. */
const PHONE_QUERY = '(max-width: 640px)';
const useIsPhoneWidth = (): boolean => {
  const [isPhone, setIsPhone] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(PHONE_QUERY).matches);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(PHONE_QUERY);
    const onChange = () => setIsPhone(mq.matches);
    onChange();
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  return isPhone;
};

const Body = styled('div', {
  maxWidth: '600px', margin: '0 auto', width: '100%', flex: 1,
});

const HeaderSection = styled('div', {
  padding: '8px', display: 'flex', gap: '12px', alignItems: 'flex-start',
});

const LogoAvatar = styled('div', {
  width: '40px', height: '40px', borderRadius: '50%', overflow: 'hidden',
  backgroundColor: '#F3F3F3', flexShrink: 0,
});

const HeaderInfo = styled('div', { flex: 1, minWidth: 0 });

const EventName = styled('h1', {
  margin: '0 0 4px', fontSize: '28px', fontWeight: 600, color: '#000', lineHeight: 1.2,
});

const InfoText = styled('div', { fontSize: '14px', color: '#000', marginBottom: '4px' });

const DateRow = styled('div', {
  display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px', fontSize: '14px',
});

const PhoneRow = styled('div', {
  display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px', fontSize: '14px',
});

const Divider = styled('div', { height: '1px', backgroundColor: '#E8E8E8', margin: '12px 0' });

const AboutSection = styled('div', { padding: '0 12px 24px' });
const AboutTitle = styled('h2', { margin: '0 0 8px', fontSize: '22px', fontWeight: 600, color: '#000' });
const AboutText = styled('div', { margin: 0, fontSize: '14px', color: '#333', lineHeight: 1.6, whiteSpace: 'pre-wrap' });

const BottomBar = styled('div', {
  padding: '22px', maxWidth: '600px', margin: '0 auto', width: '100%', boxSizing: 'border-box',
});

const ActionButton = styled('button', {
  width: '100%', padding: '16px', borderRadius: '8px', border: 'none', color: '#fff',
  fontSize: '16px', fontWeight: 600, cursor: 'pointer', transition: 'opacity 0.15s',
  ':hover': { opacity: 0.9 }, ':disabled': { opacity: 0.5, cursor: 'not-allowed' },
});

const FormContent = styled('div', {
  maxWidth: '600px', margin: '0 auto', padding: '24px 16px 60px', width: '100%', boxSizing: 'border-box',
});

const FormTitle = styled('h2', { margin: '0 0 20px', fontSize: '22px', fontWeight: 600, color: '#000' });

const FieldLabel = styled('label', {
  display: 'block', fontSize: '14px', fontWeight: 500, color: '#000', marginBottom: '8px',
});

const TextInput = styled('input', {
  width: '100%', padding: '12px 14px', borderRadius: '8px', border: '1px solid #D1D5DB',
  // 16px minimum to stop iOS Safari from auto-zooming on focus.
  fontSize: '16px', color: '#000', outline: 'none', boxSizing: 'border-box',
  ':focus': { borderColor: 'var(--tenant-primary, #000)' },
});

const PhoneInputRow = styled('div', { display: 'flex', gap: '8px' });

const CheckboxRow = styled('label', {
  display: 'flex', alignItems: 'flex-start', gap: '10px', marginTop: '16px',
  fontSize: '13px', color: '#6B7280', cursor: 'pointer', lineHeight: '1.4',
});

const PinContainer = styled('div', {
  display: 'flex', justifyContent: 'center', gap: '12px', margin: '24px 0',
});

const PinInput = styled('input', {
  width: '52px', height: '52px', textAlign: 'center', fontSize: '24px', fontWeight: 600,
  borderRadius: '8px', border: '2px solid #D1D5DB', outline: 'none',
  ':focus': { borderColor: 'var(--tenant-primary, #000)' },
});

const Counter = styled('div', { display: 'flex', alignItems: 'center', gap: '16px' });

const CounterBtn = styled('button', ({ $disabled }: { $disabled?: boolean }) => ({
  width: '36px', height: '36px', borderRadius: '50%',
  border: '2px solid var(--tenant-primary, #000)', backgroundColor: 'transparent',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  cursor: $disabled ? 'not-allowed' : 'pointer', opacity: $disabled ? 0.3 : 1,
  transition: 'all 0.15s',
}));

const CounterValue = styled('span', {
  fontSize: '20px', fontWeight: 600, minWidth: '30px', textAlign: 'center',
});

const GuestCard = styled('div', {
  border: '1px solid #E8E8E8', borderRadius: '10px', padding: '16px', marginBottom: '12px',
});

const GuestCardHeader = styled('div', {
  display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px',
  fontSize: '15px', fontWeight: 600, color: '#000',
});

const ErrorMsg = styled('div', {
  color: '#EF4444', fontSize: '13px', textAlign: 'center', marginTop: '12px',
});

const ResendLink = styled('button', {
  background: 'none', border: 'none', color: '#6B7280', fontSize: '14px',
  cursor: 'pointer', textDecoration: 'underline', marginTop: '16px',
});

const ScanCard = styled('div', {
  border: '2px dashed #D1D5DB',
  borderRadius: '12px',
  padding: '32px 16px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
  cursor: 'pointer',
  transition: 'all 0.2s',
  marginBottom: '20px',
  overflow: 'hidden',
  position: 'relative',
  ':hover': { borderColor: 'var(--tenant-primary, #000)', backgroundColor: '#FAFAFA' },
});

const ScanOverlay = styled('div', {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(255,255,255,0.92)',
  zIndex: 2000,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '16px',
});

const DocPreview = styled('img', {
  width: '100%',
  objectFit: 'contain',
  borderRadius: '12px 12px 0 0',
});

const FilledInput = styled('input', {
  width: '100%', padding: '14px 14px', borderRadius: '8px', border: 'none',
  fontSize: '15px', color: '#000', outline: 'none', boxSizing: 'border-box',
  backgroundColor: '#F3F3F3',
});

const FilledInputTitle = styled('div', {
  fontSize: '16px', fontWeight: 600, color: '#000', marginBottom: '10px',
});

const Loader = styled('div', {
  display: 'flex', justifyContent: 'center', padding: '100px 0',
});

// --- Component ---

// SAM-730: private events go detail → code → phone → …
type Step = EventStep;

export const PublicEventDetail: React.FC = () => {
  usePublicPageScroll();
  const { eventId } = useParams<{ tenantId: string; eventId: string }>();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const { tenantPrimaryColor, tenantApiKey, tenantLogo, tenantLoading, whatsappCode } = useTenantBranding();
  const isPhoneWidth = useIsPhoneWidth();

  // Each step is a browser-history entry (SAM-730): Back returns to the previous step.
  const { step, goTo: setStep, goBack } = useEventStepHistory();
  // Event code (private events, SAM-730) — checked before the phone step, sent again on register.
  const [eventCode, setEventCode] = useState('');
  // Phone
  const [phoneNumber, setPhoneNumber] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  const [deliveryMethod, setDeliveryMethod] = useState<CodeDeliveryMethod>('SMS');
  // PIN
  const [pin, setPin] = useState(['', '', '', '']);
  const [resendTimer, setResendTimer] = useState(0);
  // Cloudflare Turnstile single-use token + key for forced re-render
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaKey, setCaptchaKey] = useState(0);
  // Companions count
  const [adultsCount, setAdultsCount] = useState(1);
  const [childrenCount, setChildrenCount] = useState(0);
  // Counts for custom categories (e.g. athlete, vip) keyed by CustomCategory.key
  const [customCategoryCounts, setCustomCategoryCounts] = useState<Record<string, number>>({});
  // Ordered scan slots derived from the counts above. Built when the user
  // continues from the companions step. Slot 0 = main guest, slot 1+ = companions.
  type ScanSlot = { categoryKey: string; categoryLabel: string; nth: number };
  const [scanSlots, setScanSlots] = useState<ScanSlot[]>([]);
  // Guest info (main guest + additional adults)
  const scanFileRef = useRef<HTMLInputElement>(null);
  const [scanning, setScanning] = useState(false);
  const [scanStatus, setScanStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [scannedDocIds, setScannedDocIds] = useState<Set<string>>(new Set());
  const [scannedPreview, setScannedPreview] = useState<string>(''); // base64 preview of scanned doc
  const emptyGuest: GuestInfo = { name: '', last_name: '', document_id: '', dob: '', nationality: '', expiration_date: '' };
  const [mainGuest, setMainGuest] = useState<GuestInfo>({ ...emptyGuest });
  const [additionalGuests, setAdditionalGuests] = useState<GuestInfo[]>([]);
  const [currentGuestIndex, setCurrentGuestIndex] = useState(0); // 0 = main guest, 1+ = companions
  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [walletLoading, setWalletLoading] = useState(false);

  // Wait for tenant branding before fetching: the API rejects requests without
  // the tenant's X-Api-Key, and the key must be part of the queryKey so the
  // query re-runs once branding resolves (otherwise a keyless 401 is cached
  // and the page shows "Event not found" forever).
  const { data: event, isLoading } = useQuery({
    queryKey: ['public-event', eventId, tenantApiKey],
    queryFn: () => getPublicEvent(eventId!, tenantApiKey || undefined),
    enabled: !!eventId && !tenantLoading,
  });

  const phoneCountry = usePhoneCountry({ apiKey: tenantApiKey, enabled: !tenantLoading });
  const { countries } = phoneCountry;

  const { imageUrl: tenantLogoUrl } = useS3Image(tenantLogo);

  // Resend countdown timer
  useEffect(() => {
    if (resendTimer <= 0) return;
    const interval = setInterval(() => setResendTimer(t => t - 1), 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  const startResendTimer = useCallback(() => setResendTimer(60), []);

  const fullPhone = phoneCountry.buildFullPhone(phoneNumber);
  const isPrivate = isPrivateEvent(event);


  const companionTemplates: CompanionTemplate[] = event?.access_settings?.settings?.event || [];
  const template = companionTemplates[0];
  const maxAdults = template?.adults?.max || 10;
  const maxChildren = template?.child?.max_num || 10;
  const hasCompanionSettings = !!template;
  const customCategories = template?.categories;
  // Children is optional — only render the counter when the access_settings actually
  // configures a child range with at least one allowed slot.
  const showChildren = !!template?.child && (template.child.max_num || 0) > 0;
  // Defensive key fallback: when the template lacks a label, use the literal
  // 'adults' instead of slugifying the placeholder ('Adult' → 'adult'). Keeps
  // legacy data and freshly-persisted data in the same bucket on the dashboard.
  const adultsLabelForKey = template?.adults?.title_en;
  const adultsKey = adultsLabelForKey ? (slugify(adultsLabelForKey) || 'adults') : 'adults';

  // Seed counts to each category's min the first time the template arrives.
  useEffect(() => {
    if (!customCategories || customCategories.length === 0) return;
    setCustomCategoryCounts(prev => {
      const next = { ...prev };
      let changed = false;
      for (const cat of customCategories) {
        if (!cat?.key) continue;
        if (!(cat.key in next)) {
          next[cat.key] = Math.max(0, cat.min || 0);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [customCategories]);

  // Drop the SMS-verified token on unmount so it doesn't linger past the
  // user's session — token also auto-expires in 5 min server-side.
  useEffect(() => {
    return () => clearSmsToken();
  }, []);

  // --- Handlers ---

  const validatePhone = (num: string): string | null => {
    const digits = num.replace(/\D/g, '');
    if (!digits) return t('enterPhoneNumber');
    if (digits.length < 7) return t('phoneTooShort');
    if (digits.length > 15) return t('phoneTooLong');
    return null;
  };

  const handleSendSms = async () => {
    const phoneError = validatePhone(phoneNumber);
    if (phoneError) { setError(phoneError); return; }
    if (!smsConsent) { setError(t('acceptSmsConsent')); return; }
    const turnstileEnabled = !!import.meta.env.VITE_TURNSTILE_SITE_KEY;
    if (turnstileEnabled && !captchaToken) {
      setError(t('completeChallenge'));
      return;
    }
    setLoading(true); setError('');
    try {
      await sendSmsCode(fullPhone, tenantApiKey || undefined, captchaToken || undefined, effectiveCodeChannel(deliveryMethod, whatsappCode));
      startResendTimer();
      setStep('pin');
    } catch (err: any) {
      if (isWhatsappUnavailable(err)) {
        // The tenant lost its WhatsApp sender: stay on the phone step with SMS chosen.
        setDeliveryMethod('SMS');
        setError(t('whatsappUnavailable'));
      } else {
        setError(err.response?.data?.message || t('failedSendCode'));
      }
    } finally {
      // Single-use — drop the token and force a fresh widget for the next attempt.
      setCaptchaToken(null);
      setCaptchaKey(k => k + 1);
      setLoading(false);
    }
  };

  // SAM-737: message for a locked (429) or unavailable (503) event-code check, else null.
  const eventCodeDenialMessage = (err: unknown): string | null => {
    const denial = eventCodeDenial(err);
    if (!denial) return null;
    return denial.kind === 'locked'
      ? t('eventCodeLocked').replace('{n}', String(denial.minutes))
      : t('eventCodeUnavailable');
  };

  const handleCheckEventCode = async () => {
    const code = eventCode.trim();
    if (!code) { setError(t('enterEventCode')); return; }
    setLoading(true); setError('');
    try {
      const valid = await checkEventCode(eventId!, code, tenantApiKey || undefined);
      if (!valid) { setError(t('wrongEventCode')); return; }
      setStep('phone');
    } catch (err) {
      setError(eventCodeDenialMessage(err) ?? t('couldNotCheckCode'));
    } finally { setLoading(false); }
  };

  const handleVerifyPin = async () => {
    const code = pin.join('');
    if (code.length < 4) { setError(t('enterFullCode')); return; }
    setLoading(true); setError('');
    try {
      const res = await verifySmsCode(fullPhone, code, tenantApiKey || undefined);
      if (res.statusCode === 400) { setError(res.message || 'Invalid code'); return; }
      setStep(hasCompanionSettings ? 'companions' : 'guests');
    } catch (err: any) {
      setError(err.response?.data?.message || t('invalidCode'));
    } finally { setLoading(false); }
  };

  /**
   * Build the ordered list of scan slots based on the counts the user picked.
   * - If a custom category is flagged is_main_guest, slot 0 comes from there;
   *   the remaining of that category come right after.
   * - Otherwise slot 0 is the first adult.
   * - Children are NEVER scanned (they ride along as `children_count`).
   */
  const buildScanSlots = (): ScanSlot[] => {
    const slots: ScanSlot[] = [];
    const mainCat = customCategories?.find(c => c.is_main_guest);
    const adultsLabel = template?.adults?.title_en || 'Adult';

    const push = (key: string, label: string, count: number) => {
      for (let i = 0; i < count; i++) {
        slots.push({ categoryKey: key, categoryLabel: label, nth: i + 1 });
      }
    };

    if (mainCat?.key) {
      const mainCount = customCategoryCounts[mainCat.key] ?? Math.max(0, mainCat.min || 0);
      push(mainCat.key, mainCat.title_en || mainCat.key, mainCount);
      push(adultsKey, adultsLabel, adultsCount);
      for (const cat of customCategories || []) {
        if (!cat?.key || cat.key === mainCat.key) continue;
        const count = customCategoryCounts[cat.key] ?? Math.max(0, cat.min || 0);
        push(cat.key, cat.title_en || cat.key, count);
      }
    } else {
      push(adultsKey, adultsLabel, adultsCount);
      for (const cat of customCategories || []) {
        if (!cat?.key) continue;
        const count = customCategoryCounts[cat.key] ?? Math.max(0, cat.min || 0);
        push(cat.key, cat.title_en || cat.key, count);
      }
    }
    return slots;
  };

  const handleCompanionsContinue = () => {
    const slots = buildScanSlots();
    if (slots.length === 0) {
      setError(t('atLeastOneGuest'));
      return;
    }
    setScanSlots(slots);
    setAdditionalGuests(Array.from({ length: Math.max(0, slots.length - 1) }, () => ({ ...emptyGuest })));
    setCurrentGuestIndex(0);
    setStep('guests');
  };

  const updateAdditionalGuest = (index: number, field: keyof GuestInfo, value: string) => {
    setAdditionalGuests(prev => prev.map((g, i) => i === index ? { ...g, [field]: value } : g));
  };

  const handleSubmit = async () => {
    if (!mainGuest.name || !mainGuest.last_name || !mainGuest.document_id) {
      setError(t('completeMainGuest'));
      return;
    }
    if (!event) return;
    setLoading(true); setError('');
    try {
      const businessGuests = additionalGuests
        .map((g, i) => ({ g, slot: scanSlots[i + 1] }))
        .filter(({ g }) => g.name)
        .map(({ g, slot }) => ({
          name: g.name,
          last_name: g.last_name,
          title_es: 'Acompañante',
          idnum: g.document_id || undefined,
          // Normalise to an ISO alpha-2 code so records stay uniform regardless
          // of how OCR rendered the nationality. Falls back to the raw value.
          nationality: g.nationality ? (toNationalityIsoCode(g.nationality, countries) || g.nationality) : undefined,
          birthdate: g.dob || undefined,
          photo_url: g.photo_url || undefined,
          category_key: slot?.categoryKey,
        }));

      const result = await createEventAccess({
        business_id: event.business_id || undefined,
        event_id: event.id,
        ...(isPrivate ? { event_code: eventCode.trim() } : {}),
        start_date: event.start_date,
        end_date: event.end_date,
        children_count: childrenCount,
        main_guest: {
          name: mainGuest.name,
          last_name: mainGuest.last_name,
          tel: fullPhone,
          title_es: 'Principal',
          idnum: mainGuest.document_id || undefined,
          nationality: mainGuest.nationality ? (toNationalityIsoCode(mainGuest.nationality, countries) || mainGuest.nationality) : undefined,
          birthdate: mainGuest.dob || undefined,
          photo_url: mainGuest.photo_url || undefined,
          // Persist the slot's category so the dashboard can group correctly
          // even when the template doesn't explicitly mark is_main_guest.
          category_key: scanSlots[0]?.categoryKey,
        },
        ...(businessGuests.length > 0 ? { business_guests: businessGuests } : {}),
      }, tenantApiKey || undefined);

      const data = result?.data?.[0] || result?.data || result;
      // After a successful create the events flow hands off to PublicInvitation
      // (HashRouteHandler picks up `#/access/<id>`) so we render the SAME pass
      // card as someone arriving via the share link — single source of truth.
      const accessId = data?.id || data?.code || data?.legacy_code || data?.access_code;
      if (accessId) {
        window.location.href = `${window.location.origin}/events#/access/${accessId}`;
        return;
      }
      // Fallback: if for some reason the response didn't include an id, keep the
      // legacy local success view so the user still sees a confirmation.
      setAccessCode(data?.code || data?.legacy_code || data?.access_code || '');
      setStep('success');
    } catch (err: any) {
      // SAM-730: the code changed (or was never right) — ask for it again.
      if (isEventCodeRejected(err)) {
        setError(t('eventCodeRejected'));
        setStep('code');
        return;
      }
      const denialMessage = eventCodeDenialMessage(err);
      if (denialMessage) {
        setError(denialMessage);
        return;
      }
      setError(err.response?.data?.message || err.response?.data?.error || t('registrationFailed'));
    } finally { setLoading(false); }
  };

  const handleScanDocument = async (file: File) => {
    setScanning(true);
    setScanStatus('loading');
    setError('');
    setScannedPreview('');
    let didFail = false;
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const base64 = dataUrl.split(',')[1];
      setScannedPreview(dataUrl);

      const ocr = await analyzeDocument(base64, file.name, tenantApiKey || undefined);

      // Validate duplicate document
      const docId = ocr.number || '';
      if (docId && scannedDocIds.has(docId)) {
        setError(t('docAlreadyScanned'));
        didFail = true;
        return;
      }

      if (docId) {
        setScannedDocIds(prev => new Set(prev).add(docId));
      }

      const parsed: Partial<GuestInfo> = {
        document_id: docId,
        name: ocr.name || '',
        last_name: ocr.LastName || '',
        dob: ocr.DOB || '',
        nationality: ocr.nationality || '',
        expiration_date: ocr.expDate || '',
        // S3 key of the cropped face the OCR service uploaded
        photo_url: ocr.image || ocr.picture || '',
      };

      if (currentGuestIndex === 0) {
        setMainGuest(g => ({ ...g, ...parsed }));
      } else {
        setAdditionalGuests(prev => prev.map((g, i) =>
          i === currentGuestIndex - 1 ? { ...g, ...parsed } : g
        ));
      }
    } catch (err: any) {
      setError(t('couldNotAnalyze'));
      didFail = true;
    } finally {
      setScanStatus(didFail ? 'error' : 'success');
      window.setTimeout(() => setScanning(false), 1400);
    }
  };

  const handlePinChange = (index: number, value: string) => {
    if (value.length > 1) value = value.slice(-1);
    if (value && !/^\d$/.test(value)) return;
    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    if (value && index < 3) document.getElementById(`pin-${index + 1}`)?.focus();
  };

  const handlePinKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) document.getElementById(`pin-${index - 1}`)?.focus();
  };

  // --- Renders ---

  if (isLoading || tenantLoading) {
    return (
      <Page><Loader>
        <div style={{ width: 32, height: 32, border: '3px solid #E5E7EB', borderTopColor: tenantPrimaryColor, borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </Loader></Page>
    );
  }

  if (!event) {
    return (
      <Page>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <h2 style={{ color: '#000' }}>{t('eventNotFound')}</h2>
          <p style={{ color: '#6B7280' }}>{t('eventRemoved')}</p>
          <button onClick={() => navigate('/events')} style={{ marginTop: 16, padding: '12px 24px', borderRadius: 8, border: 'none', backgroundColor: tenantPrimaryColor, color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
            {t('viewAllEvents')}
          </button>
        </div>
      </Page>
    );
  }

  // ========== SUCCESS — QR + Wallet (matches Flutter confirmed view) ==========
  if (step === 'success') {
    const totalPersons = adultsCount + childrenCount;

    return (
      <Page style={{ backgroundColor: '#fff' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 16px', flex: 1 }}>
          {/* Tenant logo */}
          {tenantLogoUrl && (
            <img src={tenantLogoUrl} alt="" style={{ maxHeight: '60px', maxWidth: '160px', objectFit: 'contain', marginBottom: '24px' }} />
          )}

          {accessCode ? (
            <>
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(accessCode)}`}
                alt="QR Code"
                style={{ width: '250px', height: '250px' }}
              />

              <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <div style={{ fontSize: '16px', fontWeight: 600, color: '#000' }}>
                  {mainGuest.name} {mainGuest.last_name}
                </div>
                <div style={{ fontSize: '14px', color: '#6B7280', marginTop: '4px' }}>
                  {totalPersons} {totalPersons === 1 ? t('person') : t('persons')}
                  {childrenCount > 0 && ` (${adultsCount} ${t('adultsLower')}, ${childrenCount} ${t('childrenLower')})`}
                </div>
              </div>

              {/* Apple Wallet */}
              <button
                disabled={walletLoading}
                onClick={async () => {
                  setWalletLoading(true);
                  try {
                    const url = await generateWalletPass({
                      qrLink: accessCode,
                      code: accessCode,
                      type: 'Event',
                      companyName: `${mainGuest.name} ${mainGuest.last_name}`,
                      from: event.start_date || new Date().toISOString(),
                      to: event.end_date || new Date().toISOString(),
                      destination: event.address || '',
                      gate: '',
                      reservedBy: `${mainGuest.name} ${mainGuest.last_name}`,
                      persons: adultsCount + childrenCount,
                      businessName: pickLocalized(event, 'name', lang) || event.name || '',
                      location: event.address || '',
                    }, tenantApiKey || undefined);
                    if (url) {
                      // brief success animation before opening
                      await new Promise(res => setTimeout(res, 600));
                      window.open(url, '_blank');
                    }
                  } catch { /* silent */ }
                  finally { setWalletLoading(false); }
                }}
                style={{ border: 'none', background: 'none', cursor: 'pointer', marginTop: '16px', opacity: walletLoading ? 0.5 : 1 }}
              >
                <img src="/apple_wallet.png" alt="Add to Apple Wallet" style={{ height: '50px', objectFit: 'contain' }} />
              </button>

              {walletLoading && (
                <ScanOverlay>
                  <WalletPassLoader
                    status="loading"
                    size="md"
                    color={tenantPrimaryColor}
                    background="#fff"
                  />
                </ScanOverlay>
              )}

              {/* Share button */}
              <button
                onClick={async () => {
                  const shareData = {
                    title: pickLocalized(event, 'name', lang) || event.name || 'Event Pass',
                    text: `${mainGuest.name} ${mainGuest.last_name} — ${pickLocalized(event, 'name', lang) || event.name}\n${totalPersons} ${totalPersons === 1 ? t('person') : t('persons')}`,
                    url: window.location.href,
                  };
                  if (navigator.share) {
                    try { await navigator.share(shareData); } catch { /* cancelled */ }
                  } else {
                    // Fallback: copy link
                    await navigator.clipboard.writeText(`${shareData.text}\n${shareData.url}`);
                    alert(t('copiedToClipboard'));
                  }
                }}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  marginTop: '12px', padding: '12px 24px', borderRadius: '8px',
                  border: 'none', backgroundColor: tenantPrimaryColor, color: '#fff',
                  fontSize: '14px', fontWeight: 600, cursor: 'pointer', width: '100%', maxWidth: '250px',
                }}
              >
                {t('share')}
              </button>
            </>
          ) : (
            <>
              <CheckCircle size={64} color="#10B981" style={{ marginBottom: 16 }} />
              <h2 style={{ margin: '0 0 8px', fontSize: '22px', fontWeight: 600 }}>{t('registrationComplete')}</h2>
              <p style={{ color: '#6B7280', margin: '0 0 4px', fontSize: '14px', textAlign: 'center' }}>
                {pickLocalized(event, 'name', lang) || event.name}
              </p>
              <p style={{ color: '#6B7280', margin: '0', fontSize: '14px' }}>
                {totalPersons} {totalPersons === 1 ? t('person') : t('persons')}
              </p>
            </>
          )}
        </div>
      </Page>
    );
  }

  // ========== STEP 0: EVENT CODE (private events, SAM-730) ==========
  if (step === 'code') {
    return (
      <Page>
        <TopBar>
          <BackBtn onClick={() => { setError(''); goBack(); }}><ArrowLeft size={22} color="#000" /></BackBtn>
          <TopBarTitle>{t('eventCode')}</TopBarTitle>
          <div style={{ marginLeft: 'auto' }}><LanguageToggle color={tenantPrimaryColor} /></div>
        </TopBar>
        <FormContent>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', color: '#6B7280', fontSize: '14px', fontWeight: 600 }}>
            <PrivateLockIcon label={t('privateEvent')} size={16} />
            <span>{t('privateEvent')}</span>
          </div>
          <FormTitle>{t('privateEventEnterCode')}</FormTitle>
          <FieldLabel htmlFor="event-code">{t('eventCode')}</FieldLabel>
          <form onSubmit={e => { e.preventDefault(); if (!loading) handleCheckEventCode(); }}>
            <TextInput
              id="event-code"
              value={eventCode}
              onChange={e => { setEventCode(e.target.value); if (error) setError(''); }}
              autoComplete="off"
              autoCapitalize="off"
              autoFocus
              aria-invalid={!!error || undefined}
            />
            {error && <ErrorMsg role="alert">{error}</ErrorMsg>}
            <div style={{ marginTop: '24px' }}>
              <ActionButton
                type="submit"
                style={{ backgroundColor: (!eventCode.trim() || loading) ? '#D1D5DB' : tenantPrimaryColor }}
                disabled={!eventCode.trim() || loading}
              >
                {loading ? t('verifying') : t('continue')}
              </ActionButton>
            </div>
          </form>
        </FormContent>
      </Page>
    );
  }

  // ========== STEP 1: PHONE ==========
  if (step === 'phone') {
    return (
      <Page>
        <TopBar>
          <BackBtn onClick={() => { setError(''); goBack(); }}><ArrowLeft size={22} color="#000" /></BackBtn>
          <TopBarTitle>{t('phoneVerification')}</TopBarTitle>
          <div style={{ marginLeft: 'auto' }}><LanguageToggle color={tenantPrimaryColor} /></div>
        </TopBar>
        <FormContent>
          <FormTitle>{t('enterYourPhone')}</FormTitle>
          <FieldLabel>{t('phoneNumber')}</FieldLabel>
          <PhoneInputRow>
            <CountryCodeButton
              countryCode={phoneCountry.countryCode}
              flag={phoneCountry.countryFlag}
              onClick={() => phoneCountry.setShowPicker(true)}
            />
            <TextInput style={{ flex: 1, minWidth: 0 }} value={phoneNumber} onChange={e => setPhoneNumber(sanitizeNationalNumber(e.target.value))} placeholder="809 604 5871" type="tel" inputMode="numeric" autoFocus />
          </PhoneInputRow>
          {phoneCountry.showPicker && (
            <CountryPickerModal
              countries={phoneCountry.filteredCountries}
              search={phoneCountry.search}
              onSearchChange={phoneCountry.setSearch}
              onSelect={phoneCountry.selectCountry}
              onClose={() => { phoneCountry.setShowPicker(false); phoneCountry.setSearch(''); }}
              searchPlaceholder={t('searchCountry')}
            />
          )}
          {/* Channel selector: only when WhatsApp codes are on (SAM-1065); otherwise the code goes by SMS. */}
          {whatsappCodeEnabled(whatsappCode) && (
            <>
          <FieldLabel style={{ marginTop: '14px' }}>{t('howReceiveCode')}</FieldLabel>
          <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
            {([
              { id: 'SMS' as const, label: 'SMS' },
              { id: 'WHATSAPP' as const, label: 'WhatsApp' },
            ]).map(({ id, label }) => {
              const selected = deliveryMethod === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setDeliveryMethod(id)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    fontSize: '15px',
                    fontWeight: selected ? 600 : 500,
                    fontFamily: 'inherit',
                    cursor: 'pointer',
                    borderRadius: '8px',
                    border: selected ? `2px solid ${tenantPrimaryColor}` : '1px solid #D1D5DB',
                    backgroundColor: selected ? tenantPrimaryColor : '#FFFFFF',
                    color: selected ? '#FFFFFF' : '#374151',
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
            </>
          )}

          <CheckboxRow>
            <input type="checkbox" checked={smsConsent} onChange={e => setSmsConsent(e.target.checked)} style={{ marginTop: '2px', width: '18px', height: '18px', flexShrink: 0 }} />
            <span>{effectiveCodeChannel(deliveryMethod, whatsappCode) === 'WHATSAPP' ? t('agreeReceiveWhatsapp') : t('agreeReceiveSms')}</span>
          </CheckboxRow>
          {error && <ErrorMsg>{error}</ErrorMsg>}
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
            <TurnstileWidget key={captchaKey} onToken={setCaptchaToken} action="send-sms-code" />
          </div>
          <div style={{ marginTop: '24px' }}>
            <ActionButton style={{ backgroundColor: (!phoneNumber || !smsConsent || loading) ? '#D1D5DB' : tenantPrimaryColor }} disabled={!phoneNumber || !smsConsent || loading} onClick={handleSendSms}>
              {loading ? t('sending') : t('continue')}
            </ActionButton>
          </div>
        </FormContent>
      </Page>
    );
  }

  // ========== STEP 2: PIN ==========
  if (step === 'pin') {
    return (
      <Page>
        <TopBar>
          <BackBtn onClick={goBack}><ArrowLeft size={22} color="#000" /></BackBtn>
          <TopBarTitle>{t('verifyCode')}</TopBarTitle>
        </TopBar>
        <FormContent style={{ textAlign: 'center' }}>
          <FormTitle>{t('enterVerificationCode')}</FormTitle>
          <p style={{ color: '#6B7280', margin: '0 0 8px', fontSize: '14px' }}>{t('weSentCodeTo')} <strong>{fullPhone}</strong></p>
          <PinContainer>
            {pin.map((digit, i) => (
              <PinInput key={i} id={`pin-${i}`} value={digit} onChange={e => handlePinChange(i, e.target.value)} onKeyDown={e => handlePinKeyDown(i, e)} maxLength={1} inputMode="numeric" autoFocus={i === 0} />
            ))}
          </PinContainer>
          {error && <ErrorMsg>{error}</ErrorMsg>}
          <ActionButton style={{ backgroundColor: (pin.join('').length < 4 || loading) ? '#D1D5DB' : tenantPrimaryColor }} disabled={pin.join('').length < 4 || loading} onClick={handleVerifyPin}>
            {loading ? t('verifying') : t('continue')}
          </ActionButton>
          {/* Re-mount the captcha so a fresh token is available for "Resend code". */}
          {resendTimer === 0 && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '12px' }}>
              <TurnstileWidget key={`resend-${captchaKey}`} onToken={setCaptchaToken} action="send-sms-code" />
            </div>
          )}
          <ResendLink
            disabled={resendTimer > 0 || (!!import.meta.env.VITE_TURNSTILE_SITE_KEY && !captchaToken)}
            style={{
              opacity: (resendTimer > 0 || (!!import.meta.env.VITE_TURNSTILE_SITE_KEY && !captchaToken)) ? 0.5 : 1,
              cursor: (resendTimer > 0 || (!!import.meta.env.VITE_TURNSTILE_SITE_KEY && !captchaToken)) ? 'default' : 'pointer',
            }}
            onClick={() => {
              if (resendTimer > 0) return;
              if (!!import.meta.env.VITE_TURNSTILE_SITE_KEY && !captchaToken) return;
              setPin(['', '', '', '']);
              setError('');
              handleSendSms();
            }}
          >
            {resendTimer > 0 ? `${t('resendCodeIn')} ${resendTimer}s` : t('resendCode')}
          </ResendLink>
        </FormContent>
      </Page>
    );
  }

  // ========== STEP 3: COMPANIONS COUNT ==========
  if (step === 'companions') {
    return (
      <Page>
        <TopBar>
          <BackBtn onClick={goBack}><ArrowLeft size={22} color="#000" /></BackBtn>
          <TopBarTitle>{t('companions')}</TopBarTitle>
          <div style={{ marginLeft: 'auto' }}><LanguageToggle color={tenantPrimaryColor} /></div>
        </TopBar>
        <FormContent>
          <FormTitle>{t('howManyAttend')}</FormTitle>

          {template?.adults && (
            <div style={{ marginBottom: 24 }}>
              <FieldLabel>{pickLocalized(template.adults, 'title', lang) || t('adults')}</FieldLabel>
              <Counter>
                <CounterBtn $disabled={adultsCount <= (template.adults.min || 1)} onClick={() => setAdultsCount(a => Math.max(template.adults!.min || 1, a - 1))}><Minus size={16} /></CounterBtn>
                <CounterValue>{adultsCount}</CounterValue>
                <CounterBtn $disabled={adultsCount >= maxAdults} onClick={() => setAdultsCount(a => Math.min(maxAdults, a + 1))}><Plus size={16} /></CounterBtn>
              </Counter>
              {pickLocalized(template.adults, 'subtitle', lang) && <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 6 }}>{pickLocalized(template.adults, 'subtitle', lang)}</div>}
            </div>
          )}

          {/* Custom categories (athlete, vip, spectator, etc.) configured in access_settings */}
          {customCategories?.map(cat => {
            if (!cat?.key) return null;
            const value = customCategoryCounts[cat.key] ?? Math.max(0, cat.min || 0);
            const min = Math.max(0, cat.min || 0);
            const max = cat.max || 10;
            const setValue = (next: number) => setCustomCategoryCounts(prev => ({ ...prev, [cat.key]: next }));
            return (
              <div key={cat.key} style={{ marginBottom: 24 }}>
                <FieldLabel>{pickLocalized(cat, 'title', lang) || cat.key}</FieldLabel>
                <Counter>
                  <CounterBtn $disabled={value <= min} onClick={() => setValue(Math.max(min, value - 1))}><Minus size={16} /></CounterBtn>
                  <CounterValue>{value}</CounterValue>
                  <CounterBtn $disabled={value >= max} onClick={() => setValue(Math.min(max, value + 1))}><Plus size={16} /></CounterBtn>
                </Counter>
                {pickLocalized(cat, 'subtitle', lang) && <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 6 }}>{pickLocalized(cat, 'subtitle', lang)}</div>}
              </div>
            );
          })}

          {/* Children stays at the bottom and only renders if configured (max > 0) */}
          {showChildren && template?.child && (
            <div style={{ marginBottom: 24 }}>
              <FieldLabel>{pickLocalized(template.child, 'name', lang) || t('children')}</FieldLabel>
              <Counter>
                <CounterBtn $disabled={childrenCount <= (template.child.min_num || 0)} onClick={() => setChildrenCount(c => Math.max(template.child!.min_num || 0, c - 1))}><Minus size={16} /></CounterBtn>
                <CounterValue>{childrenCount}</CounterValue>
                <CounterBtn $disabled={childrenCount >= maxChildren} onClick={() => setChildrenCount(c => Math.min(maxChildren, c + 1))}><Plus size={16} /></CounterBtn>
              </Counter>
              {pickLocalized(template.child, 'subtitle', lang) && <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 6 }}>{pickLocalized(template.child, 'subtitle', lang)}</div>}
            </div>
          )}

          <ActionButton style={{ backgroundColor: tenantPrimaryColor }} onClick={handleCompanionsContinue}>{t('continue')}</ActionButton>
        </FormContent>
      </Page>
    );
  }

  // ========== STEP 4: GUEST INFORMATION — scan + review ==========
  if (step === 'guests') {
    // Slots define the full scanning sequence (main + every companion).
    // Falls back to the legacy 1-main-guest derivation if the slots array
    // hasn't been built yet (e.g. event has no companion settings at all).
    const slotsForGuests = scanSlots.length > 0
      ? scanSlots
      : [{ categoryKey: 'adults', categoryLabel: 'Main Guest', nth: 1 } as ScanSlot];
    const totalGuests = slotsForGuests.length;
    const isMainGuest = currentGuestIndex === 0;
    const guest = isMainGuest ? mainGuest : additionalGuests[currentGuestIndex - 1];
    const isLastGuest = currentGuestIndex >= totalGuests - 1;
    const currentSlot = slotsForGuests[currentGuestIndex];

    const updateCurrentGuest = (field: keyof GuestInfo, value: string) => {
      if (isMainGuest) {
        setMainGuest(g => ({ ...g, [field]: value }));
      } else {
        updateAdditionalGuest(currentGuestIndex - 1, field, value);
      }
    };

    const isFormValid = guest.name && guest.last_name && guest.document_id;

    const validateGuest = (): string | null => {
      if (!guest.name.trim()) return t('nameRequired');
      if (!guest.last_name.trim()) return t('lastNameRequired');
      if (!guest.document_id.trim()) return t('idRequired');

      // Check duplicate document across all guests
      const allDocs: string[] = [];
      if (mainGuest.document_id) allDocs.push(mainGuest.document_id.trim().toLowerCase());
      additionalGuests.forEach(g => { if (g.document_id) allDocs.push(g.document_id.trim().toLowerCase()); });
      const currentDoc = guest.document_id.trim().toLowerCase();
      const occurrences = allDocs.filter(d => d === currentDoc).length;
      if (occurrences > 1) return t('docAlreadyUsed');

      // Check duplicate name
      const allNames: string[] = [];
      allNames.push(`${mainGuest.name.trim()} ${mainGuest.last_name.trim()}`.toLowerCase());
      additionalGuests.forEach(g => { if (g.name) allNames.push(`${g.name.trim()} ${g.last_name.trim()}`.toLowerCase()); });
      const currentName = `${guest.name.trim()} ${guest.last_name.trim()}`.toLowerCase();
      const nameOccurrences = allNames.filter(n => n === currentName).length;
      if (nameOccurrences > 1) return t('nameExists');

      return null;
    };

    const handleGuestContinue = () => {
      const guestError = validateGuest();
      if (guestError) { setError(guestError); return; }
      setError('');
      if (!isFormValid) return;
      if (isLastGuest) {
        handleSubmit();
      } else {
        setCurrentGuestIndex(i => i + 1);
        setScannedPreview('');
      }
    };

    const handleGuestBack = () => {
      if (currentGuestIndex > 0) {
        setCurrentGuestIndex(i => i - 1);
      } else {
        goBack();
      }
    };

    // Title shows the slot's category and ordinal, e.g. "Athlete 1", "Adult 2".
    // Main guest gets a "(Main Guest)" suffix so the user knows their pass is built around it.
    const slotTitle = currentSlot
      ? `${currentSlot.categoryLabel} ${currentSlot.nth}${isMainGuest ? ` • ${t('mainGuest')}` : ''}`
      : (isMainGuest ? t('mainGuest') : `${t('companion')} ${currentGuestIndex}`);
    const scanTitle = isMainGuest
      ? `${t('scanYourId')}${currentSlot ? ` (${currentSlot.categoryLabel})` : ''}`
      : `${t('scanIdFor')} ${currentSlot?.categoryLabel || t('companion')} ${currentSlot?.nth ?? currentGuestIndex}`;

    return (
      <Page>
        <TopBar>
          <BackBtn onClick={handleGuestBack}><ArrowLeft size={22} color="#000" /></BackBtn>
          <TopBarTitle>{slotTitle}</TopBarTitle>
        </TopBar>
        <FormContent>
          <GuestScanForm
            guest={guest}
            onChange={updateCurrentGuest}
            scanning={scanning}
            scanStatus={scanStatus}
            scannedPreview={scannedPreview}
            onScanFile={handleScanDocument}
            scanTitle={scanTitle}
            error={error}
            tenantPrimaryColor={tenantPrimaryColor}
            autoFocusFirstField
          />
        </FormContent>

        <BottomBar>
          <ActionButton
            style={{ backgroundColor: (!isFormValid || loading) ? '#D1D5DB' : tenantPrimaryColor }}
            disabled={!isFormValid || loading}
            onClick={handleGuestContinue}
          >
            {loading ? t('registering') : isLastGuest ? t('confirmInvitation') : t('saveContinue')}
          </ActionButton>
        </BottomBar>
      </Page>
    );
  }

  // ========== EVENT DETAIL ==========
  const hero = pickEventHero(event, isPhoneWidth);
  return (
    <PublicEventDetailView
      event={event}
      heroImageKey={hero.key}
      heroFullImage={hero.full}
      onBack={() => navigate('/events')}
      onRegister={() => { setError(''); setStep(isPrivate ? 'code' : 'phone'); }}
    />
  );
};

// ========== EVENT DETAIL VIEW (shared with the admin preview, SAM-714) ==========

export interface PublicEventDetailViewProps {
  /** Event as the public API returns it — the admin preview feeds its form data here. */
  event: Partial<Omit<PublicEvent, 'id'>> & { id?: string | number; [key: string]: any };
  /** S3 key of the hero image (pickEventHero: mobile banner on a phone, the wide image elsewhere). */
  heroImageKey?: string;
  onBack?: () => void;
  onRegister?: () => void;
  /**
   * Show the hero image whole, at its own aspect ratio, instead of the 250px
   * wide crop. Used for the 4:5 mobile banner on phones (public page at phone
   * width, admin preview in Phone mode); desktop keeps the wide hero.
   */
  heroFullImage?: boolean;
  /** Extra styles for the outer page (the admin preview frame drops the 100vh min-height). */
  pageStyle?: React.CSSProperties;
}

/**
 * The event detail screen (hero, logo, name, dates, phone, About, Register),
 * without data fetching. PublicEventDetail renders it with the public API's
 * event; the admin's event preview (/eventsad/:id/preview) renders it with the
 * editor's data so admins see exactly what capcana.app will show.
 */
export const PublicEventDetailView: React.FC<PublicEventDetailViewProps> = ({
  event, heroImageKey, heroFullImage, onBack, onRegister, pageStyle,
}) => {
  const { t, lang } = useLang();
  const { tenantPrimaryColor, tenantLogo } = useTenantBranding();
  const { imageUrl: heroUrl } = useS3Image(heroImageKey);
  const { imageUrl: eventLogoUrl } = useS3Image(event.logo_url || tenantLogo);

  const formatDate = (iso?: string) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };
  const isExpired = (iso?: string) => iso ? new Date(iso) < new Date() : false;
  const isActive = event.end_date ? !isExpired(event.end_date) : event.active !== false;
  const isPrivate = isPrivateEvent(event);

  return (
    <Page style={pageStyle}>
      <TopBar>
        <BackBtn onClick={onBack}><ArrowLeft size={22} color="#000" /></BackBtn>
        <TopBarTitle>{t('details')}</TopBarTitle>
        <div style={{ marginLeft: 'auto' }}><LanguageToggle color={tenantPrimaryColor} /></div>
      </TopBar>
      <HeroImage style={heroFullImage && heroUrl ? { height: 'auto' } : undefined}>
        {heroUrl ? (
          <img
            src={heroUrl}
            alt={event.name || ''}
            style={heroFullImage
              ? { width: '100%', height: 'auto', display: 'block' }
              : { width: '100%', height: '250px', objectFit: 'cover' }}
          />
        ) : (
          <div style={{ width: '100%', height: '250px', backgroundColor: tenantPrimaryColor }} />
        )}
      </HeroImage>
      <Body>
        <HeaderSection>
          <LogoAvatar>{eventLogoUrl && <img src={eventLogoUrl} alt="" style={{ width: '40px', height: '40px', objectFit: 'cover' }} />}</LogoAvatar>
          <HeaderInfo>
            <EventName>{pickLocalized(event, 'name', lang) || event.name}</EventName>
            {isPrivate && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', fontSize: '13px', fontWeight: 600, color: '#6B7280' }}>
                <PrivateLockIcon label={t('privateEventLock')} size={14} />
                <span>{t('privateEvent')}</span>
              </div>
            )}
            {event.address && <InfoText>{event.address}</InfoText>}
            {event.start_date && (
              <DateRow><span>{t('startAt')} </span><strong style={{ color: isExpired(event.start_date) ? '#EF4444' : '#16A34A' }}>{formatDate(event.start_date)}</strong></DateRow>
            )}
            {event.end_date && (
              <DateRow><span>{t('endAt')} </span><strong>{formatDate(event.end_date)}</strong></DateRow>
            )}
            {event.phone_number && (
              <PhoneRow><PhoneIcon size={16} /><a href={`tel:${event.phone_number}`} style={{ color: '#000', textDecoration: 'underline' }}>{event.phone_number}</a></PhoneRow>
            )}
          </HeaderInfo>
        </HeaderSection>
        <Divider />
        <AboutSection>
          <AboutTitle>{t('about')}</AboutTitle>
          <AboutText>
            {renderMarkdownLite(
              pickLocalized(event, 'description', lang) || pickLocalized(event, 'short_description', lang) || event.description || ''
            )}
          </AboutText>
        </AboutSection>
      </Body>
      {isActive && (
        <BottomBar>
          <ActionButton style={{ backgroundColor: tenantPrimaryColor }} onClick={onRegister}>{t('register')}</ActionButton>
        </BottomBar>
      )}
    </Page>
  );
};
